import {
	AfterViewInit,
	Directive,
	ElementRef,
	inject,
	ViewChild,
} from '@angular/core';
import { PaginatedObject } from '@marmon/frontend/pagination';
import { Entity } from '@marmon/shared/types/common';
import { PageSize, SortMod } from '@marmon/shared/types/pagination';
import {
	BehaviorSubject,
	combineLatest,
	debounceTime,
	filter,
	fromEvent,
	map,
	of,
	startWith,
	Subject,
	switchMap,
	takeUntil,
	tap,
	throttleTime,
} from 'rxjs';
import { TableBaseComponent } from '../table-base/table-base.component';
import { DestroyService } from '../../services';

const ScrollThreshold = 100;

@Directive()
export class InfiniteScrollBaseComponent<
	ObjectType extends Pick<Entity, 'id'>,
	AdditionalOptions,
>
	extends TableBaseComponent<ObjectType>
	implements AfterViewInit
{
	readonly destroy$ = inject(DestroyService);

	@ViewChild('scroller', { read: ElementRef })
	readonly scrollerRef!: ElementRef<HTMLDivElement>;

	readonly paginatedObject = new PaginatedObject<ObjectType>();

	override readonly size$ = new BehaviorSubject<PageSize>(50);
	readonly additional$ = new BehaviorSubject<AdditionalOptions | null>(null);
	readonly disableInfiniteScroll: boolean = false;

	readonly initView$ = new Subject<void>();

	loadingMore = false;
	readonly scrollStream$ = this.initView$
		.pipe(
			takeUntil(this.destroy$),
			filter(() => {
				const hasScroller = !!this.scrollerRef?.nativeElement;
				if (!hasScroller)
					console.error(
						'The scroller could not be found for infinite scroll',
					);
				return hasScroller && !this.disableInfiniteScroll;
			}),
			switchMap(() =>
				fromEvent(this.scrollerRef.nativeElement, 'scroll'),
			),
			filter(() => {
				const isLoading = this.loadingMore || this.loading$.value;
				return !isLoading && this.paginatedObject.hasMore;
			}),
			map(() => {
				const { clientHeight, scrollTop, scrollHeight } =
					this.scrollerRef.nativeElement;
				const bottom = clientHeight + scrollTop;
				const delta = scrollHeight - bottom;
				return delta < ScrollThreshold;
			}),
			filter((invoke) => !!invoke),
			debounceTime(0),
			tap(() => {
				if (!this.loadingMore) {
					this.loadingMore = true;
					this.refetch$.next();
				}
			}),
		)
		.subscribe();

	readonly request$ = (
		options: [
			PageSize,
			keyof ObjectType | null,
			SortMod,
			AdditionalOptions | null,
			void,
		],
	) => of(options).pipe(map(() => new PaginatedObject<ObjectType>()));

	readonly data$ = combineLatest([
		this.size$,
		this.sorter$,
		this.direction$,
		this.additional$,
		this.refetch$.pipe(throttleTime(500), startWith(undefined)),
	])
		.pipe(
			takeUntil(this.destroy$),
			debounceTime(100),
			tap(() => {
				if (!this.loadingMore) {
					this.loading$.next(true);
				}
			}),
			switchMap((options) => this.request$(options)),
			tap(() => {
				this.loading$.next(false);
				this.loadingMore = false;
			}),
			startWith(this.paginatedObject),
		)
		.subscribe();

	ngAfterViewInit() {
		this.initView$.next();
	}
}
